import {ComponentType} from '@angular/cdk/portal';
import {Component, Inject, OnInit, Type} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {NavigationHelperService} from '@application/helper/navigation-helper/navigation-helper.service';
import {NavigationNewItemData} from '@application/helper/navigation-helper/navigation-new-item-data.interface';
import {PageTypeEnum} from '@application/helper/navigation-helper/page-type.enum';
import {PlanningItemHelper} from '@application/helper/planning-prototype/planning-item-helper';
import {AddComponentInput} from '@application/helper/routing/add-component-input.interface';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {IdName} from '@domain/id-name';
import {OrderCarpet} from '@domain/planning-prototype/carpet/order-carpet';
import {RunCarpet} from '@domain/planning-prototype/carpet/run-carpet';
import {GenericRun} from '@domain/planning-prototype/generic-run.interface';
import {PlanningEquipment} from '@domain/planning-prototype/planning-equipment';
import {PlanningItem} from '@domain/planning-prototype/planning-item';
import {RunPlastic} from '@domain/planning-prototype/plastic/run-plastic';
import {Tool} from '@domain/planning-prototype/plastic/tool';
import {RunTufting} from '@domain/planning-prototype/tufting/run-tufting';
import {TuftProduct} from '@domain/planning-prototype/tufting/tuft-product';
import {RunWeaving} from '@domain/planning-prototype/weaving/run-weaving';
import {WeaveProduct} from '@domain/planning-prototype/weaving/weave-product';
import {Subscription} from '@domain/profile/subscription';
import {CreelMap} from '@domain/textile-data/creel/creel-map';
import {WeaveQuality} from '@domain/textile-data/machine-quality/weave-quality';
import {Tool as UtilTool} from '@domain/utilities/tool/tool';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {CREELS, Creels} from '@infrastructure/http/creel/creels';
import {MACHINE_QUALITIES, MachineQualities} from '@infrastructure/http/machine-quality/machine-qualities';
import {HttpToolsService} from '@infrastructure/http/tool/http-tools.service';
import {HttpTuftProductsService} from '@infrastructure/http/tuft-product/http-tuft-products.service';
import {GetAllTuftProductsParameters} from '@infrastructure/http/tuft-product/tuft-products.interface';
import {WEAVE_PRODUCT, WeaveProducts} from '@infrastructure/http/weave-product/weave-products.interface';
import {SelectCreelComponent} from '@presentation/components/select-creel/select-creel.component';
import {SelectMachineQualityComponent} from '@presentation/components/select-machine-quality/select-machine-quality.component';
import {SelectWeaveProductComponent} from '@presentation/pages/texfab/production-order-lite/add/select-weave-product/select-weave-product.component';
import {SelectTuftProductComponent} from '@presentation/pages/texfab/tuft-production-order/add/select-tuft-product/select-tuft-product.component';
import {OverviewListCreel} from '@presentation/pages/textile-data/creel/overview/overview-list-creel';
import {OverviewListMachineQuality} from '@presentation/pages/textile-data/machine-quality/overview/overview-list-machine-quality';
import {OverviewListTuftProduct} from '@presentation/pages/textile-data/tuft-product/overview/overview-list-tuft-product';
import {OverviewListWeaveProduct} from '@presentation/pages/textile-data/weave-product/overview/overview-list-weave-product';
import {AddToolPageComponent} from '@presentation/pages/utilities/tool/add/add-tool-page.component';
import {ToolOverviewService} from '@presentation/pages/utilities/tool/overview/tool-overview.service';
import {
  AgGridRowSelection,
  AssertionUtils,
  BaseComponent,
  ContentSwitcherNavigationInput,
  CrudObjectSelectionDialogComponent,
  CrudObjectSelectionDialogInput,
  CrudOverviewDataBuilderFactoryService,
  CrudOverviewDataConfig,
  DialogBuilderFactoryService,
  DialogComponentData,
  GridOptionsBuilder,
  MathUtils,
  NavigationHelperPrototypeService,
  SelectGridDialog,
  TranslateService
} from '@vdw/angular-component-library';
import {ColDef} from 'ag-grid-community';
import {DateTime} from 'luxon';
import {duration} from 'moment';
import {Observable, debounceTime, distinctUntilChanged, filter, map, merge, startWith, takeUntil} from 'rxjs';
import {PlanningPrototypeContextService} from '../../utilities/planning-prototype-context.service';
import {NavigationPlanningRunSidebar} from './navigation-planning-run-sidebar.interface';

type ObjectSelectionType = Tool | UtilTool;

@Component({
  selector: 'app-planning-run-sidebar',
  templateUrl: './planning-run-sidebar.component.html',
  styleUrls: ['./planning-run-sidebar.component.scss']
})
export class PlanningRunSidebarComponent extends BaseComponent implements OnInit {
  public readonly QUALITY_CHIP_VALUE_KEY = '%(technicalNameWithVersion)';
  public readonly CREEL_BASE_ROUTER_LINK = RouteUtils.paths.texStyle.creel.editCreel.absolutePath;
  public readonly QUALITY_BASE_ROUTER_LINK = RouteUtils.paths.texStyle.machineQuality.editMachineQuality.absolutePath;
  public readonly WEAVE_PRODUCT_BASE_ROUTER_LINK = RouteUtils.paths.texStyle.weaveProduct.editWeaveProduct.absolutePath;
  public readonly TUFT_PRODUCT_BASE_ROUTER_LINK = RouteUtils.paths.texStyle.tuftProduct.editTuftProduct.absolutePath;
  public readonly TOOL_BASE_ROUTER_LINK = RouteUtils.paths.utilities.tool.editTool.absolutePath;
  public readonly UNKNOWN_VALUE_KEY = 'PLANNING.UNKNOWN';
  private readonly SELECT_OBJECT_KEY = 'GENERAL.ACTIONS.SELECT_OBJECT';

  public readonly runForm = new FormGroup({
    creel: new FormControl<OverviewListCreel>(null),
    machineQuality: new FormControl<IdName>(null),
    weaveProduct: new FormControl<WeaveProduct>(null),
    tuftProduct: new FormControl<TuftProduct>(null),
    tool: new FormControl<Tool | UtilTool>(null),
    planningItems: new FormControl<PlanningItem[]>([]),
    earliestStartDate: new FormControl<DateTime>(null),
    creelMappingFormula: new FormControl<string>(null),
    minimumDuration: new FormControl<number>(null, [Validators.min(0)])
  });

  public isCarpet = false;
  public isWeaving = false;
  public isTufting = false;
  public isPlastic = false;
  public isCompleted = false;
  public isStarted = false;
  public editCreelMappingActive = false;
  public createItemMode = false;
  public run: GenericRun;
  private toolObjectSelectionConfig: CrudOverviewDataConfig<Tool | UtilTool>;
  private currentSubscription: Subscription;

  public get machine(): PlanningEquipment {
    return this.planningContext.machinesByPlanningItem[this.run.draftId];
  }

  public set machine(value: PlanningEquipment) {
    this.planningContext.machinesByPlanningItem[this.run.draftId] = value;
  }

  public constructor(
    private readonly planningContext: PlanningPrototypeContextService,
    private readonly translate: TranslateService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly toolOverviewService: ToolOverviewService,
    private readonly crudOverviewBuilder: CrudOverviewDataBuilderFactoryService<ObjectSelectionType>,
    private readonly router: Router,
    private readonly tools: HttpToolsService,
    private readonly navigationHelperService: NavigationHelperService<NavigationPlanningRunSidebar>,
    private readonly navigationHelperPrototypeService: NavigationHelperPrototypeService,
    private readonly tuftProducts: HttpTuftProductsService,
    @Inject(CREELS) private readonly creels: Creels,
    @Inject(MACHINE_QUALITIES) private readonly qualities: MachineQualities,
    @Inject(AUTHENTICATION) private authentication: Authentication,
    @Inject(WEAVE_PRODUCT) private readonly weaveProducts: WeaveProducts
  ) {
    super();
  }

  public ngOnInit(): void {
    this.currentSubscription = this.authentication.getCurrentSubscription();
    this.resetFormOnSelectedPlanningItemChanged();
    this.changeProductEnabledStateOnChildrenChanged();
    this.applyCreelMappingOnSelectedCreelChanged();
    this.changePlanningItemsEnabledStateOnProductChanged();
    this.initObjectSelectionConfigs();

    let prevState = this.navigationHelperService.getPartialState<NavigationPlanningRunSidebar>(Object.keys(this.getEmptyPlanningRunSidebar()));
    if (!AssertionUtils.isNullOrUndefined(prevState)) {
      this.runForm.patchValue({
        creel: prevState.creel,
        machineQuality: prevState.machineQuality,
        weaveProduct: prevState.weaveProduct,
        tuftProduct: prevState.tuftProduct,
        tool: prevState.tool,
        planningItems: prevState.planningItems,
        earliestStartDate: prevState.earliestStartDate ? DateTime.fromJSDate(prevState.earliestStartDate) : null,
        creelMappingFormula: prevState.creelMappingFormula,
        minimumDuration: prevState.minimumDuration
      });
      this.editCreelMappingActive = prevState.editCreelMappingActive;
      this.isCarpet = prevState.isCarpet;
      this.isWeaving = prevState.isWeaving;
      this.isTufting = prevState.isTufting;
      this.isPlastic = prevState.isPlastic;
      this.isCompleted = prevState.isCompleted;
      this.isStarted = prevState.isStarted;
      this.createItemMode = prevState.createItemMode1;
      this.run = prevState.run;
      this.machine = prevState.machineData;
      this.planningContext.planningLinesByMachine[this.machine.id].planningItems = prevState.planningLinesByMachine;
      this.planningContext.sortedPlanningItemForecasts = prevState.sortedPlanningItemForecasts;
    }

    const getFunctionsBasedOnDialogComponent = new Map<any, any>([
      [SelectCreelComponent, {loadNewItem: ({newItemId}: any): void => this.setCreel(newItemId), reopenDialog: (): void => this.selectCreel()}],
      [SelectMachineQualityComponent, {loadNewItem: ({newItemId}: NavigationNewItemData): void => this.setQuality(newItemId), reopenDialog: (): void => this.selectMachineQuality()}],
      [SelectWeaveProductComponent, {loadNewItem: ({newItemId}: NavigationNewItemData): void => this.setWeaveProduct(newItemId), reopenDialog: (): void => this.selectWeaveProduct()}],
      [SelectTuftProductComponent, {loadNewItem: ({newItemId}: NavigationNewItemData): void => this.setTuftProduct(newItemId), reopenDialog: (): void => this.selectTuftProduct()}]
    ]);

    this.navigationHelperService.setNewItemOrReopenDialogIfPresent(
      ({newItemId}: NavigationNewItemData, {dialogComponent}: DialogComponentData) => getFunctionsBasedOnDialogComponent.get(dialogComponent).loadNewItem({newItemId}),
      ({dialogComponent}: DialogComponentData) => getFunctionsBasedOnDialogComponent.get(dialogComponent).reopenDialog()
    );
  }

  public selectCreel(): void {
    this.openSelectDialog(SelectCreelComponent, 'TEXTILE_DATA.CREEL.CREEL')
      .pipe(
        takeUntil(this.unSubscribeOnViewDestroy),
        filter((selectedCreels: OverviewListCreel[]) => !AssertionUtils.isEmpty(selectedCreels))
      )
      .subscribe(([selectedCreel]: OverviewListCreel[]) => {
        this.runForm.controls.creel.setValue(selectedCreel);
        this.runForm.controls.creel.markAsDirty();
      });
  }

  public onNavigationHelperDestroy(): void {
    this.navigationHelperService.savePartialState<NavigationPlanningRunSidebar>({
      creel: this.runForm.get('creel').value,
      machineQuality: this.runForm.get('machineQuality').value,
      weaveProduct: this.runForm.get('weaveProduct').value,
      tuftProduct: this.runForm.get('tuftProduct').value,
      tool: this.runForm.get('tool').value,
      planningItems: this.runForm.get('planningItems').value,
      earliestStartDate: this.runForm.get('earliestStartDate').value?.toJSDate(),
      creelMappingFormula: this.runForm.get('creelMappingFormula').value,
      minimumDuration: this.runForm.get('minimumDuration').value,
      editCreelMappingActive: this.editCreelMappingActive,
      isCarpet: this.isCarpet,
      isWeaving: this.isWeaving,
      isTufting: this.isTufting,
      isPlastic: this.isPlastic,
      isCompleted: this.isCompleted,
      isStarted: this.isStarted,
      createItemMode1: this.createItemMode,
      run: this.run,
      machineData: this.machine,
      planningLinesByMachine: this.planningContext.planningLinesByMachine[this.machine.id].planningItems,
      sortedPlanningItemForecasts: this.planningContext.sortedPlanningItemForecasts
    });
  }

  public selectMachineQuality(): void {
    this.openSelectDialog(SelectMachineQualityComponent, 'TEXTILE_DATA.MACHINE_QUALITY.MACHINE_QUALITY')
      .pipe(
        takeUntil(this.unSubscribeOnViewDestroy),
        filter((selectedQualities: OverviewListMachineQuality[]) => !AssertionUtils.isEmpty(selectedQualities))
      )
      .subscribe(([selectedQuality]: OverviewListMachineQuality[]) => {
        this.runForm.controls.machineQuality.setValue(new IdName(selectedQuality.id, selectedQuality.technicalNameWithVersion));
        this.runForm.controls.machineQuality.markAsDirty();
      });
  }

  public selectWeaveProduct(): void {
    this.openSelectDialog(SelectWeaveProductComponent, 'TEXTILE_DATA.WEAVE_PRODUCT.WEAVE_PRODUCT')
      .pipe(
        takeUntil(this.unSubscribeOnViewDestroy),
        filter((selectedWeaveProduct: OverviewListWeaveProduct | '') => !AssertionUtils.isNullOrUndefined(selectedWeaveProduct) && selectedWeaveProduct !== '')
      )
      .subscribe((selectedWeaveProduct: OverviewListWeaveProduct) => {
        this.runForm.controls.weaveProduct.setValue(WeaveProduct.fromOverviewListWeaveProduct(selectedWeaveProduct));
        this.runForm.controls.weaveProduct.markAsDirty();
      });
  }

  public selectTuftProduct(): void {
    this.openSelectDialog(SelectTuftProductComponent, 'TEXTILE_DATA.TUFT_PRODUCT.TUFT_PRODUCT')
      .pipe(
        takeUntil(this.unSubscribeOnViewDestroy),
        filter((selectedTuftProduct: OverviewListTuftProduct | '') => !AssertionUtils.isNullOrUndefined(selectedTuftProduct) && selectedTuftProduct !== '')
      )
      .subscribe((selectedTuftProduct: OverviewListTuftProduct) => {
        this.runForm.controls.tuftProduct.setValue(TuftProduct.fromOverviewListTuftProduct(selectedTuftProduct));
        this.runForm.controls.tuftProduct.markAsDirty();
      });
  }

  public selectTool(): void {
    this.openObjectSelectionDialog<Tool | UtilTool>(this.runForm.controls.tool, this.toolObjectSelectionConfig, AddToolPageComponent);
  }

  public delete(): void {
    this.planningContext.delete(this.run, this.planningContext.planningLinesByMachine[this.machine.id].planningItems, this.machine);
  }

  public isCreelSelected(): boolean {
    return this.isCarpet && !AssertionUtils.isNullOrUndefined(this.runForm.controls.creel.value);
  }

  public hasOrders(): boolean {
    return !AssertionUtils.isEmpty(this.runForm.controls.planningItems.value);
  }

  public getUnknownItemPlaceholder(): string {
    if (!this.hasOrders() || !this.isCompleted) {
      return null;
    }
    return this.translate.instant(this.UNKNOWN_VALUE_KEY);
  }

  public saveChanges(): void {
    this.planningContext.selectItem(null);
    if (!this.runForm.dirty) {
      return;
    }
    this.run.earliestStartDate = this.runForm.controls.earliestStartDate.value?.toJSDate();
    this.run.minimumDuration = duration(this.runForm.controls.minimumDuration.value, 'hours').format('d.hh:mm:ss', {trim: 'both', stopTrim: 'h m s'});
    this.savePlanningItems();

    if (this.run instanceof RunCarpet) {
      this.saveCarpetProperties(this.run);
    }

    if (this.run instanceof RunWeaving) {
      this.run.weaveProduct = this.runForm.controls.weaveProduct.value;
    }

    if (this.run instanceof RunTufting) {
      this.run.tuftProduct = this.runForm.controls.tuftProduct.value;
    }

    if (this.run instanceof RunPlastic) {
      const toolValue = this.runForm.controls.tool.value;
      this.run.tool = !AssertionUtils.isNullOrUndefined(toolValue) ? new Tool(toolValue.id, toolValue.name, null, null) : null;
    }

    this.planningContext.markAsChanged(this.machine);
  }

  public createItem(): void {
    this.planningContext.itemCreationPlaceholder = null;
    this.planningContext.addItemToList(
      this.run,
      this.planningContext.planningLinesByMachine[this.machine.id].planningItems,
      this.planningContext.sortedPlanningItemForecasts[this.run.draftId].estimatedStart
    );
    this.saveChanges();
  }

  public confirmCreelMapChanges(): void {
    this.editCreelMappingActive = false;
    const creel = this.runForm.controls.creel.value;

    const creelMapsWithOnlyChangedPositions = creel.mappingForCurrentPositionOnMachine
      .map((creelMap: CreelMap) => ({
        coloredYarnSetId: creelMap.coloredYarnSetId,
        creelPositions: creelMap.creelPositions.filter((position: {creelPosition: number; currentPositionOnMachine: number}) => position.creelPosition !== position.currentPositionOnMachine)
      }))
      .filter(({creelPositions}: CreelMap) => !AssertionUtils.isEmpty(creelPositions));

    const creelMappingFormula = creelMapsWithOnlyChangedPositions
      .map(
        (creelMapChanges: CreelMap) =>
          `${creelMapChanges.coloredYarnSetId}:${creelMapChanges.creelPositions
            .map((position: {creelPosition: number; currentPositionOnMachine: number}) => `${position.creelPosition}.${position.currentPositionOnMachine}`)
            .join(',')}`
      )
      .join('&');

    this.runForm.controls.creelMappingFormula.setValue(creelMappingFormula);
    this.runForm.controls.creelMappingFormula.markAsDirty();
  }

  public discardCreelMapChanges(): void {
    this.editCreelMappingActive = false;
    this.runForm.controls.creelMappingFormula.setValue(this.runForm.controls.creelMappingFormula.value);
  }

  public canCreateOrderForRun(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.run.id) && !this.run.isCompleted;
  }

  public addOrderForRun(): void {
    if (this.run instanceof RunCarpet) {
      this.router.navigate([RouteUtils.paths.texFab.productionOrder.addProductionOrderForRun.absolutePath], {
        state: {
          runId: this.run.id,
          machineQualityId: this.run.quality.id,
          creelId: this.run.creel.id,
          machineId: this.machine.id,
          startRoute: RouteUtils.paths.texFab.planningPrototype.absolutePath
        }
      });
    }
  }

  private getEmptyPlanningRunSidebar(): NavigationPlanningRunSidebar {
    return {
      creel: null,
      machineQuality: null,
      weaveProduct: null,
      tuftProduct: null,
      tool: null,
      planningItems: null,
      earliestStartDate: null,
      creelMappingFormula: null,
      minimumDuration: null,
      isCarpet: null,
      isWeaving: null,
      isTufting: null,
      isPlastic: null,
      isCompleted: null,
      isStarted: null,
      editCreelMappingActive: null,
      createItemMode1: null,
      run: null,
      machineData: null,
      planningLinesByMachine: null,
      sortedPlanningItemForecasts: null
    };
  }

  private resetForm(run: GenericRun): void {
    this.run = run;
    this.editCreelMappingActive = false;
    this.createItemMode = run === this.planningContext.itemCreationPlaceholder;
    this.runForm.reset();
    const planningItems = [...run.planningItems];
    planningItems.sort(PlanningItem.compareBySequenceNumber);
    this.runForm.controls.planningItems.setValue(planningItems);
    this.isCarpet = this.isWeaving = this.isTufting = this.isPlastic = false;
    this.isCompleted = !AssertionUtils.isNullOrUndefined(run.actualEndDate);
    this.isStarted = !AssertionUtils.isNullOrUndefined(run.actualStartDate);
    this.runForm.controls.earliestStartDate.setValue(run.earliestStartDate ? DateTime.fromJSDate(run.earliestStartDate) : null);
    this.runForm.controls.minimumDuration.setValue(MathUtils.round(duration(run.minimumDuration).asHours(), 2));

    this.runForm.controls.creel.removeValidators([Validators.required]);
    this.runForm.controls.machineQuality.removeValidators([Validators.required]);
    this.runForm.controls.weaveProduct.removeValidators([Validators.required]);
    this.runForm.controls.tuftProduct.removeValidators([Validators.required]);

    if (this.isStarted) {
      this.runForm.controls.earliestStartDate.disable();
      this.runForm.controls.minimumDuration.disable();
    } else {
      this.runForm.controls.earliestStartDate.enable();
      this.runForm.controls.minimumDuration.enable();
    }

    if (run instanceof RunCarpet) {
      this.isCarpet = true;
      this.runForm.controls.machineQuality.addValidators([Validators.required]);
      this.runForm.controls.creel.addValidators([Validators.required]);
      this.runForm.controls.creelMappingFormula.setValue(run.creelMappingFormula);
      this.runForm.controls.machineQuality.setValue(run.quality);
      if (!AssertionUtils.isNullOrUndefined(run.creel?.id)) {
        this.runForm.controls.creel.markAsDirty();
        this.creels
          .getById(run.creel.id)
          .pipe(takeUntil(this.unSubscribeOnViewDestroy))
          .subscribe((creel: OverviewListCreel) => this.runForm.controls.creel.setValue(creel));
      }
    }

    if (run instanceof RunWeaving) {
      this.isWeaving = true;
      this.runForm.controls.weaveProduct.addValidators([Validators.required]);
      this.runForm.controls.weaveProduct.setValue(run.weaveProduct);
    }

    if (run instanceof RunTufting) {
      this.isTufting = true;
      this.runForm.controls.tuftProduct.addValidators([Validators.required]);
      this.runForm.controls.tuftProduct.setValue(run.tuftProduct);
    }

    if (run instanceof RunPlastic) {
      this.isPlastic = true;
      this.runForm.controls.tool.setValue(run.tool);
    }
  }

  private getCreelMappingFromFormula(creel: OverviewListCreel, creelMappingFormula: string): void {
    creel.mappingForCurrentPositionOnMachine = creel.getOriginalMappingForCurrentPositionOnMachine();
    if (AssertionUtils.isEmpty(creelMappingFormula)) {
      return;
    }

    const colorSets = creelMappingFormula.split('&');
    for (const colorSet of colorSets) {
      const [coloredYarnSetId, colorSetData] = colorSet.split(':');
      const mapToUpdate = creel.mappingForCurrentPositionOnMachine.find((mapping: CreelMap) => mapping.coloredYarnSetId === Number(coloredYarnSetId));
      if (AssertionUtils.isNullOrUndefined(mapToUpdate)) {
        continue;
      }
      for (const creelPositionsData of colorSetData.split(',')) {
        const [creelPosition, currentPositionOnMachine] = creelPositionsData.split('.').map(Number);
        const positionToUpdate = mapToUpdate.creelPositions.find((position: {creelPosition: number}) => position.creelPosition === creelPosition);
        positionToUpdate.currentPositionOnMachine = currentPositionOnMachine;
      }
    }
  }

  private applyCreelMappingOnSelectedCreelChanged(): void {
    merge(this.runForm.controls.creel.valueChanges, this.runForm.controls.creelMappingFormula.valueChanges)
      .pipe(
        takeUntil(this.unSubscribeOnViewDestroy),
        debounceTime(0),
        startWith(undefined),
        map(() => [this.runForm.controls.creel.value, this.runForm.controls.creelMappingFormula.value]),
        filter(([creel, _]: [OverviewListCreel, string]) => !AssertionUtils.isNullOrUndefined(creel))
      )
      .subscribe(([creel, creelMapping]: [OverviewListCreel, string]) => this.getCreelMappingFromFormula(creel, creelMapping));
  }

  private changeProductEnabledStateOnChildrenChanged(): void {
    this.runForm.controls.planningItems.valueChanges
      .pipe(
        takeUntil(this.unSubscribeOnViewDestroy),
        startWith(undefined),
        map(() => this.runForm.controls.planningItems.value),
        distinctUntilChanged()
      )
      .subscribe((childItems: PlanningItem[]) => {
        const action = AssertionUtils.isEmpty(childItems) ? 'enable' : 'disable';
        this.runForm.controls.creel[action]();
        this.runForm.controls.machineQuality[action]();
        this.runForm.controls.weaveProduct[action]();
        this.runForm.controls.tuftProduct[action]();
        this.runForm.controls.tool[action]();
      });
  }

  private changePlanningItemsEnabledStateOnProductChanged(): void {
    merge(
      this.runForm.controls.creel.valueChanges,
      this.runForm.controls.machineQuality.valueChanges,
      this.runForm.controls.weaveProduct.valueChanges,
      this.runForm.controls.tuftProduct.valueChanges,
      this.runForm.controls.tool.valueChanges
    )
      .pipe(takeUntil(this.unSubscribeOnViewDestroy), debounceTime(0), startWith(undefined))
      .subscribe(() => {
        const planningItemsForm = this.runForm.controls.planningItems;

        const allowCarpetPlanning =
          this.isCarpet && !AssertionUtils.isNullOrUndefined(this.runForm.controls.creel.value) && !AssertionUtils.isNullOrUndefined(this.runForm.controls.machineQuality.value);
        const allowWeavingPlanning = this.isWeaving && !AssertionUtils.isNullOrUndefined(this.runForm.controls.weaveProduct.value);
        const allowTuftingPlanning = this.isTufting && !AssertionUtils.isNullOrUndefined(this.runForm.controls.tuftProduct.value);
        const allowPlasticPlanning = this.isPlastic;

        if (!this.isCompleted && (allowCarpetPlanning || allowWeavingPlanning || allowTuftingPlanning || allowPlasticPlanning)) {
          planningItemsForm.enable();
        } else {
          planningItemsForm.disable();
        }
      });
  }

  private resetFormOnSelectedPlanningItemChanged(): void {
    this.planningContext.selectedItemChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy), filter(PlanningItemHelper.isRunItem)).subscribe((run: GenericRun) => this.resetForm(run));
  }

  private openSelectDialog<T>(component: ComponentType<SelectGridDialog>, objectKey: string): Observable<T> {
    return this.dialogBuilderFactoryService.getBuilder().openSelectGridDialog(component, {
      title: this.translate.instant(this.SELECT_OBJECT_KEY, {
        object: this.translate.instant(objectKey, {count: 1}).toLowerCase()
      }),
      rowSelection: AgGridRowSelection.SINGLE,
      machineId: this.machine.id,
      allowEmptySelection: false
    });
  }

  private openObjectSelectionDialog<T extends ObjectSelectionType>(control: FormControl<T>, config: CrudOverviewDataConfig<T>, addPage?: Type<any>): void {
    config.selectedObjects = [control.value];

    this.navigationHelperPrototypeService
      .navigateWithContentSwitcher<T[]>({
        componentInfo: {
          component: CrudObjectSelectionDialogComponent,
          componentInputs: {
            config,
            newClickedEntry: {componentInfo: {component: addPage, componentInputs: {pageType: PageTypeEnum.ADD, canShowHeader: true} as AddComponentInput}} as ContentSwitcherNavigationInput
          } as CrudObjectSelectionDialogInput
        }
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((selectedObjects: T[]) => {
        if (!AssertionUtils.isEmpty(selectedObjects)) {
          control.setValue(selectedObjects[0]);
          control.markAsDirty();
        }
      });
  }

  private initObjectSelectionConfigs(): void {
    this.toolObjectSelectionConfig = this.initObjectSelectionConfig('UTILITIES.TOOL.TOOL', this.toolOverviewService.getColumnDefs(false).colDefs, GridIdentifier.TOOL_OVERVIEW, this.tools.getAll());
  }

  private initObjectSelectionConfig<T extends ObjectSelectionType>(
    translationKey: string,
    columns: ColDef<T>[],
    gridIdentifier: GridIdentifier,
    dataObservable: Observable<T[]>
  ): CrudOverviewDataConfig<T> {
    return this.crudOverviewBuilder
      .getBuilder<T>()
      .withEntityName(translationKey)
      .withGridOptions(columns, gridIdentifier, null, (gridOptions: GridOptionsBuilder) => {
        if (gridIdentifier === GridIdentifier.TOOL_OVERVIEW) {
          gridOptions = this.toolOverviewService.getDefaultGridOptionsBuilder(gridOptions);
        }
        return gridOptions.withRowSelection(false, false).build();
      })
      .withRowData(dataObservable)
      .build();
  }

  private saveCarpetProperties(run: RunCarpet): void {
    const creel = this.runForm.controls.creel.value;
    const quality = this.runForm.controls.machineQuality.value;
    run.creel = !AssertionUtils.isNullOrUndefined(creel) ? new IdName(creel.id, creel.name) : null;
    run.quality = !AssertionUtils.isNullOrUndefined(quality) ? new IdName(quality.id, quality.name) : null;
    run.creelMappingFormula = this.runForm.controls.creelMappingFormula.value;
    for (const order of this.run.planningItems) {
      if (order instanceof OrderCarpet) {
        order.creelMappingFormula = run.creelMappingFormula;
      }
    }
  }

  private savePlanningItems(): void {
    this.run.planningItems = this.runForm.controls.planningItems.value;
    let sequenceNumber = 1;
    for (const item of this.run.planningItems) {
      if (item.sequenceNumber > 0) {
        item.sequenceNumber = sequenceNumber++;
      }
    }
  }

  private setWeaveProduct(id: number): void {
    this.weaveProducts
      .getAllForMachineId(this.machine.id)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((weaveProducts: OverviewListWeaveProduct[]) => {
        let newWeaveProduct = weaveProducts.find((weaveProduct: OverviewListWeaveProduct) => weaveProduct.id === id);
        this.runForm.controls.machineQuality.setValue(new IdName(newWeaveProduct.id, newWeaveProduct.name));
        this.runForm.controls.machineQuality.markAsDirty();
      });
  }

  private setCreel(id: number): void {
    this.creels
      .getPossibleCreelsForMachine(this.machine.id)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((creels: OverviewListCreel[]) => {
        let newCreel = creels.find((creel: OverviewListCreel) => creel.id === id);
        if (!AssertionUtils.isNullOrUndefined(newCreel)) {
          this.runForm.controls.creel.setValue(newCreel);
          this.runForm.controls.creel.markAsDirty();
        }
      });
  }

  private setQuality(id: number): void {
    this.qualities
      .getPossibleMachineQualitiesForMachine(this.machine.id)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((qualities: WeaveQuality[]) => {
        let newQuality = qualities.find((quality: WeaveQuality) => quality.id === id);
        this.runForm.controls.machineQuality.setValue(new IdName(newQuality.id, newQuality.technicalNameWithVersion));
        this.runForm.controls.machineQuality.markAsDirty();
      });
  }

  private setTuftProduct(id: number): void {
    const parameters: Partial<GetAllTuftProductsParameters> = {showOnlyLatestVersion: true};
    this.tuftProducts
      .getAllForMachineId(this.machine.id, parameters)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((tuftProducts: OverviewListTuftProduct[]) => {
        let newTuftProduct = tuftProducts.find((tuftProduct: OverviewListTuftProduct) => tuftProduct.id === id);
        this.runForm.controls.tuftProduct.setValue(TuftProduct.fromOverviewListTuftProduct(newTuftProduct));
        this.runForm.controls.tuftProduct.markAsDirty();
      });
  }
}
